import {
  Component,
  Input,
  ViewChild,
  OnInit,
  AfterViewInit,
} from '@angular/core';
import { finalize } from 'rxjs';
import { TruckSearch } from '@app/trucks/models/truck-search.model';
import { MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AppService } from '@app/app.service';
import { TruckViewPublicDialogComponent } from '@app/trucks/truck-view-public-dialog/truck-view-public-dialog.component';
import { DatePipe } from '@angular/common';
import { AuthService } from '@app/auth/auth.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-saved-trucks',
  imports: [
    MatButtonModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatProgressSpinnerModule,
    MatIconModule,
    DatePipe,
    MatTooltipModule,
    CommonModule,
],
  templateUrl: './saved-trucks.component.html',
  styleUrl: './saved-trucks.component.scss',
})

export class SavedTrucksComponent implements OnInit, AfterViewInit {
  @Input({ required: true }) search!: TruckSearch;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  loading = false;

  results = new MatTableDataSource<any>([]);

  isMobile = window.innerWidth < 768;
  itemsPerPage = this.isMobile ? 10 : 25;

  desktopColumns: string[] = [
    'is_saved',
    'age',
    'available_dates',
    'origin_city',
    'origin_state',
    'max_weight',
    'axle_count',
    'well_length',
    'carrier',
    'carrier_base_state',
  ];

  mobileColumns = [
    'mobile'
  ];

  displayedColumns = this.isMobile ? this.mobileColumns : this.desktopColumns;

  @HostListener('window:resize')
  onResize(): void {
    this.isMobile = window.innerWidth < 768;
    this.displayedColumns = this.isMobile
      ? this.mobileColumns
      : this.desktopColumns;
  }

  constructor(
    private dialog: MatDialog,
    private appService: AppService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    if (!this.authService.userCanSearchTrucks()) {
      this.appService.navigateToDefaultUrl();
      return;
    }

    this.getSavedTrucks();
  }

  ngAfterViewInit(): void {
    this.results.paginator = this.paginator;
    this.results.sort = this.sort;

    this.results.sortingDataAccessor = (item: any, property: string) => {
      switch (property) {
        case 'age':
          return new Date(item.refreshed_at).getTime();

        case 'origin_deadhead_distance':
          return item.origin_deadhead_distance ?? 999999;

        case 'origin':
          return item.origin_city ?? '';

        case 'available_dates':
          return item.available_start_date ?? '';

        case 'carrier':
          return item.carrier_name ?? '';

        case 'carrier_base_state':
          return item.carrier_base_state ?? '';

        default:
          return item[property];
      }
    };
  }

  getSavedTrucks(successMessage: boolean = false): void {
    this.loading = true;

    this.appService
      .get('saved/trucks')
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res) => {
          this.results.data = res.data ?? [];
          if (successMessage) {
            if (res.data.length === 0) {
              this.appService.warningSnackBar(res.message);
            } else {
              this.appService.successSnackBar(res.message);
            }
          }
        },
        error: (err) => {
          this.appService.showSnackBar(err.error.message, 'error');
        },
      });
  }

  viewTruckDetails(truck: any): void {

    if (truck.deleted_at || !truck.visible) {
      this.appService.showSnackBar('This truck is no longer available.', 'warning');
      return;
    }

    this.dialog.open(TruckViewPublicDialogComponent, {
      width: '900px',
      maxWidth: '900px',
      data: { 
        truck: truck,
        toggleSavedTruck: () => this.toggleSavedTruck(truck)
       },
    });
  }

  toggleSavedTruck(truck: any): void {
    const originalValue = truck.is_saved;
    truck.is_saved = !originalValue;

    this.appService
      .patch(`saved/trucks/${truck.id}/toggle-saved`, {})
      .subscribe({
        next: (res) => {
          truck.is_saved = res.data.is_saved;
          this.sortFavoritesOnTop();
        },
        error: (err) => {
          truck.is_saved = originalValue;
          this.appService.handleError(err);
        },
      });
  }

  toFeetAndInches(totalInches: number): string {
    const feet = Math.floor(totalInches / 12);
    const inches = totalInches % 12;

    return `${feet}-${inches}`;
  }

  private sortFavoritesOnTop(): void {
    this.results.data = [...this.results.data].sort((a, b) => {
      const savedCompare = Number(b.is_saved) - Number(a.is_saved);

      if (savedCompare !== 0) {
        return savedCompare;
      }

      return new Date(b.refreshed_at).getTime() - new Date(a.refreshed_at).getTime();
    });
  }

  formatAge(refreshedSince: string): string {
    if (!refreshedSince) {
      return '';
    }

    const [hours, minutes] = refreshedSince.split(':').map(Number);

    if (hours < 24) {
      return refreshedSince;
    }

    const days = Math.floor(hours / 24);

    return `${days} day${days > 1 ? 's' : ''}`;
  }
}
